import React, { useEffect, useState } from "react";
import { Box, Pagination, Stack } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useMutation } from "@tanstack/react-query";
import { updateYoYEmissions, updateYoYConsumption, updateYoYFinancials } from "api";
import { COLORS, Loading } from "components";
import { chartColors, commonStyles } from "../const";
import { YoyChartProps } from "../types";

export const EstimatesChartYearOverYear = ({
  chartTitle,
  currentMainTab,
  subTabName,
  searchFilters,
  yoyLengthFilter,
}: YoyChartProps) => {
  const [yoYData, setYoYData] = useState<any>([]);
  const apiFn = currentMainTab === "GHG Emissions" ? updateYoYEmissions : currentMainTab === "Consumption" ? updateYoYConsumption : updateYoYFinancials;
  const { mutateAsync, isLoading } = useMutation(apiFn);
  const noDataAvailable = !isLoading && Object.values(yoYData).every(value =>
    typeof value === "object" && value !== null && Object.keys(value).length === 0
  );
  const { estimate } = yoYData;

  const [currentPage, setcurrentPage] = useState<number>(1);
  const paginationLength: number = 4;

  const isEmissionsChart = subTabName === "location" || subTabName === "market";
  const isConsumption = currentMainTab === "Consumption";

  const data: any[] = estimate ? Object.values(estimate) : [];

  const estimateCategoriesXAxis = estimate ? Object.keys(estimate) : [];

  const emissionType = isEmissionsChart ? `${subTabName}BasedEmission` : isConsumption ? "consumption" : "amount";

  const fiscalYearsData: any = data.reduce((acc: any, entry: any) => {
    entry.forEach((item: any) => {
      if (!acc[item.fiscalYear]) {
        acc[item.fiscalYear] = [];
      }
      acc[item.fiscalYear].push({
        estimate: item.type,
        [emissionType]: item[emissionType] ?? 0,
      });
    });
    return acc;
  }, {});

  const series = Object.values(fiscalYearsData).map((entries: any) => {
    const emissionsArray = new Array(estimateCategoriesXAxis.length).fill(0);

    entries.forEach((entry: any) => {
      const estimateIndex = estimateCategoriesXAxis.indexOf(entry.estimate);
      if (estimateIndex !== -1) {
        emissionsArray[estimateIndex] = entry[emissionType];
      }
    });

    return emissionsArray;
  });

  const seriesData = Object.keys(fiscalYearsData).map((fiscalYear, index) => ({
    type: "column",
    name: `FY${fiscalYear}`,
    data: series[index],
    color: chartColors[index % chartColors.length],
    fillOpacity: 0.3,
    lineWidth: 6,
    marker: { enabled: false },
  }));

  const isPaginationDisabled: boolean = estimateCategoriesXAxis.length <= 4;
  const pages = estimateCategoriesXAxis.length % paginationLength !== 0 ? Math.trunc(estimateCategoriesXAxis.length / paginationLength) + 1 : estimateCategoriesXAxis.length / paginationLength;

  const unitYAxis = isEmissionsChart ? "tCO2e" : data[0]?.[0]?.unit ?? data[0]?.[0]?.currency ?? "No Data Available";

  useEffect(() => {
    const fetchYoyData = async () => {
      setYoYData({})
      await mutateAsync({
        YoYRequest: {
          ...searchFilters,
          yearsToCompare: yoyLengthFilter,
        },
        chartType: 'estimate'
      }, {
        onSuccess: (response: any) => {
          setYoYData(response.data);
        },
        onError: () => {
          // implement error handling
        },
      })
    };

    fetchYoyData();
  }, [searchFilters, yoyLengthFilter, mutateAsync])


  const chartOptions = {
    chart: {
      spacing: [30, 50, 20, 40],
      width: null,
    },
    title: {
      text: `${chartTitle}${noDataAvailable ? ": No Data Available" : ''}`,
      align: "left",
      margin: 40,
      style: {
        ...commonStyles,
        color: COLORS.Mainblue,
        fontSize: "20px",
      },
    },
    xAxis: {
      categories: estimateCategoriesXAxis.length > 4 ? estimateCategoriesXAxis.slice((currentPage * paginationLength) - paginationLength, (currentPage * paginationLength)) : estimateCategoriesXAxis,
      labels: {
        style: commonStyles,
      },
      startOnTick: false,
      endOnTick: false,
    },
    yAxis: {
      title: {
        text: unitYAxis,
        style: commonStyles,
        offset: 50
      },
      labels: {
        style: commonStyles,
      },
    },
    series: seriesData.map((serie: any) => ({
      ...serie,
      data: serie.data.length > 4 ? serie.data.slice((currentPage * paginationLength) - paginationLength, (currentPage * paginationLength)) : serie.data,
    })),
    plotOptions: {
      line: {
        dataLabels: {
          enabled: true,
          style: {
            fontWeight: "bold",
            textOutline: "",
          },
          formatter(this: Highcharts.TooltipFormatterContextObject) {
            return `<b>${Number(this!.y?.toFixed(2)).toLocaleString('en-US')}</b>`;
          },
        },
      },
      column: {
        dataLabels: {
          enabled: true,
          style: {
            fontWeight: "bold",
            textOutline: "",
          },
          formatter(this: Highcharts.TooltipFormatterContextObject) {
            return `<b>${Number(this!.y?.toFixed(2)).toLocaleString('en-US')}</b>`;
          },
        },
      },
    },
    legend: {
      title: {
        style: commonStyles,
      },
      align: "left",
      verticalAlign: "bottom",
      x: 20,
      margin: 30,
    },
    tooltip: {
      shared: true,
      borderRadius: 8,
      shadow: false,
      padding: 14,
      useHTML: true,
      outside: true,
      positioner(labelWidth: any, labelHeight: any, point: any) {
        return {
          x: point.plotX,
          y: point.plotY - labelHeight,
        };
      },
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        return `
              <div style="font-size: 12px; padding: 8px; display: flex; flex-direction: column; gap: 5px;">
                <strong>${this!.x}</strong>
                ${this!.points
            ?.map(
              (point) => `
                  <div style="display: flex; align-items: center; justify-content: space-between; width: 180px;">
                    <div style="display: flex; align-items: center; gap: 6px;">
                      <span style="width: 10px; height: 10px; background: ${point.series.color}; border-radius: 50%; display: inline-block;"></span>
                      <span>${point.series.name}:</span>
                    </div>
                    <span style="font-weight: bold;">${Number(point.y!.toFixed(2)).toLocaleString('en-US')} ${unitYAxis}</span>
                  </div>
                `
            ).join("")}
              </div>
            `;
      },
    },
    credits: {
      enabled: false,
    },
  };

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      gap: 3,
      fontFamily: "Inter",
      position: "relative",
      "& > div": {
        borderRadius: 2,
      },
    }}>
      {isLoading && <Box sx={{ height: 300, position: "absolute", zIndex: 99999, right: "50%" }}>
        <Loading />
      </Box>}
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
      />
      {isPaginationDisabled
        ? ""
        : <Box sx={{
          position: "absolute",
          bottom: 22,
          right: 50,
        }}>
          <Stack spacing={2}>
            <Pagination
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "gray",
                },
                "& .Mui-selected": {
                  fontWeight: "600",
                  color: COLORS.Mainblue,
                }
              }}
              count={pages}
              page={currentPage}
              size="small"
              onChange={(event: any, page: number) => setcurrentPage(page)} />
          </Stack>
        </Box>
      }
    </Box>
  );
};
